/**
 * "New project" modal dialog. Reusable across the sidebar and the
 * workspace overview page.
 *
 * Key auto-derives from name until edited. Project keys must be 2-6
 * uppercase alphanumeric, starting with a letter.
 */

import { useState } from 'react'

interface CreateProjectDialogProps {
  onClose: () => void
  onSubmit: (input: { name: string; key: string; color?: string }) => Promise<void> | void
  busy: boolean
}

export function CreateProjectDialog({ onClose, onSubmit, busy }: CreateProjectDialogProps) {
  const [name, setName] = useState('')
  const [keyValue, setKeyValue] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [color, setColor] = useState('#5B5BD6')

  const sanitized = name.toUpperCase().replace(/[^A-Z0-9]/g, '')
  const firstLetter = (sanitized.match(/[A-Z]/) ?? [''])[0]
  const rest = sanitized.replace(/^[A-Z]/, '').replace(/[^A-Z0-9]/g, '').slice(0, 5)
  const derivedKey = firstLetter + rest || 'PROJ'
  const effectiveKey = keyTouched ? keyValue : derivedKey

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="card w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-1 text-base font-semibold">New project</h2>
        <p className="mb-4 text-xs text-surface-500">
          Projects hold tasks, columns, and sprints. Default columns will be created.
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            const n = name.trim()
            const k = effectiveKey.trim().toUpperCase()
            if (!n || !k) return
            await onSubmit({ name: n, key: k, color })
          }}
          className="space-y-3"
        >
          <label className="block">
            <span className="text-xs font-medium text-surface-600 dark:text-surface-300">Name</span>
            <input
              autoFocus
              required
              maxLength={120}
              className="input mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Mobile App"
            />
          </label>
          <div className="flex items-end gap-2">
            <label className="block flex-1">
              <span className="text-xs font-medium text-surface-600 dark:text-surface-300">Key</span>
              <input
                required
                maxLength={6}
                className="input mt-1 font-mono uppercase"
                value={effectiveKey}
                onChange={(e) => {
                  setKeyTouched(true)
                  setKeyValue(e.target.value.toUpperCase())
                }}
                placeholder="DEMO"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-surface-600 dark:text-surface-300">Color</span>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1 h-9 w-12 cursor-pointer rounded border border-surface-300"
              />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !name.trim()}>
              {busy ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}